import React, { useEffect } from 'react';
import { Controller, type FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { CardElement, useElements } from '@stripe/react-stripe-js';
import { StripeCardElementOptions } from '@stripe/stripe-js';

import { ModalMultiStepProps } from '~/models/deposits/ModalMultiStepProps';
import { Button } from '~/vinovest-design-system/components';
import { Alert } from '~/vinovest-design-system/components/Form/components/Alert';

type Inputs = {
    name: string;
    cardNumber: string;
};

interface CreditCardFormProps extends ModalMultiStepProps {
    onCreditCardInfoEntered: ({ name, cardNumber }: Inputs) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setStripeCardElement: any;
    loading: boolean;
    error: unknown;
}

/** Collect user credit card information */
export const CreditCardForm = ({
    onCreditCardInfoEntered,
    loading,
    error,
    setStripeCardElement,
    setStep
}: CreditCardFormProps) => {
    const elements = useElements();

    const {
        register,
        handleSubmit,
        control,
        trigger,
        formState: { errors, isDirty, isValid, isSubmitting }
    } = useForm({ mode: 'onChange', reValidateMode: 'onBlur' });

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        setStripeCardElement(elements?.getElement(CardElement));
    }, [elements, setStripeCardElement]);

    const onSubmit: SubmitHandler<FieldValues> = (data) => {
        onCreditCardInfoEntered({ name: data.name as string, cardNumber: data.cardNumber as string });
    };

    return (
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        <form onSubmit={handleSubmit(onSubmit)}>
            <section>
                <Controller
                    control={control}
                    name={'cardNumber'}
                    rules={{ required: 'Credit card information is required' }}
                    render={({ field: { onChange, onBlur } }) => (
                        <CardElement
                            className={
                                'border border-neutrals-300 font-favorite my-2 p-4 rounded text-16 w-full appearance-none'
                            }
                            options={CARD_ELEMENT_OPTIONS}
                            onChange={onChange}
                            onBlur={onBlur}
                        />
                    )}
                />
            </section>
            {Boolean(error) && (
                <Alert classnames={'my-4'} type={'error'}>
                    Sorry, your credit card could not be set up at this time.
                </Alert>
            )}
            <section>
                <input
                    placeholder={'Name on card'}
                    className={
                        'border border-neutrals-300 font-sans my-2 p-4 rounded text-16 w-full appearance-none placeholder-neutrals-500'
                    }
                    type={'text'}
                    {...register('name', {
                        required: 'Your name is required',
                        maxLength: { value: 27, message: 'Name is too long' },
                        onChange: () => {
                            if (errors.name) {
                                void trigger('name');
                            }
                        }
                    })}
                />
            </section>
            {errors?.name && (
                <Alert classnames={'my-4'} type={'error'}>
                    {errors?.name?.message}
                </Alert>
            )}

            <div className={'flex flex-col mt-10'}>
                <Button
                    variant={'green'}
                    type={'submit'}
                    classnames={'mb-5'}
                    disabled={!isValid || isSubmitting || !isDirty || loading}
                >
                    {loading ? 'Connecting ...' : 'Continue'}
                </Button>
                <Button variant={'secondary'} onClick={() => setStep(0)}>
                    Back
                </Button>
            </div>
        </form>
    );
};

const CARD_ELEMENT_OPTIONS: StripeCardElementOptions = {
    iconStyle: 'solid',
    style: {
        base: {
            iconColor: '#A86D37',
            color: '#242E35',
            fontWeight: '500',
            fontFamily: 'var(--font-vinovest-mono), Roboto, Open Sans, Segoe UI, sans-serif',
            fontSize: '16px',
            fontSmoothing: 'antialiased',
            ':-webkit-autofill': {
                color: '#767A7F'
            },
            '::placeholder': {
                color: '#767A7F'
            }
        },
        complete: {
            iconColor: '#27AE60',
            color: '#27AE60'
        },
        invalid: {
            iconColor: '#FF4D00',
            color: '#FF4D00'
        }
    }
};
